import React, { useRef, useState, useEffect } from 'react';
import SignaturePad from 'signature_pad';
import { Button } from '../../../components/ui/Button';

interface SignatureCanvasProps {
  onSave: (signatureData: string) => void;
  onCancel: () => void;
  userName?: string;
  userTitle?: string;
}

export const SignatureCanvas: React.FC<SignatureCanvasProps> = ({
  onSave,
  onCancel,
  userName,
  userTitle,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);
  const [isEmpty, setIsEmpty] = useState(true);

  useEffect(() => {
    if (canvasRef.current) {
      const signaturePad = new SignaturePad(canvasRef.current, {
        backgroundColor: 'rgb(255, 255, 255)',
        penColor: 'rgb(0, 0, 0)',
      });

      signaturePadRef.current = signaturePad;

      const handleBegin = () => {
        setIsEmpty(false);
      };

      signaturePad.addEventListener('beginStroke', handleBegin);

      // Set canvas size
      const resizeCanvas = () => {
        const canvas = canvasRef.current;
        if (canvas) {
          const ratio = Math.max(window.devicePixelRatio || 1, 1);
          canvas.width = canvas.offsetWidth * ratio;
          canvas.height = canvas.offsetHeight * ratio;
          canvas.getContext('2d')?.scale(ratio, ratio);
          signaturePad.clear();
        }
      };

      resizeCanvas();
      window.addEventListener('resize', resizeCanvas);

      return () => {
        signaturePad.removeEventListener('beginStroke', handleBegin);
        window.removeEventListener('resize', resizeCanvas);
      };
    }
  }, []);

  const handleClear = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
      setIsEmpty(true);
    }
  };

  const handleSave = async () => {
    if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
      return;
    }

    const dataURL = signaturePadRef.current.toDataURL('image/png');

    try {
      const maybePromise: unknown = onSave(dataURL);
      if (maybePromise && typeof (maybePromise as any).then === 'function') {
        await (maybePromise as Promise<unknown>);
      }
    } catch {
      // Swallow errors from consumer to avoid blocking UI;
      // calling code is responsible for user-facing error handling.
    }
  };

  return (
    <div className="space-y-4">
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-white">
        <canvas
          ref={canvasRef}
          className="w-full h-48 cursor-crosshair touch-none"
          style={{ touchAction: 'none' }}
        />
      </div>

      {userName && (
        <div className="text-center">
          <p className="font-medium">{userName}</p>
          {userTitle && <p className="text-sm text-gray-600">{userTitle}</p>}
        </div>
      )}

      <div className="flex justify-between gap-2">
        <Button variant="outline" onClick={handleClear}>
          Clear
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isEmpty}>
            Save Signature
          </Button>
        </div>
      </div>

      <p className="text-xs text-gray-500 text-center">
        Please sign in the box above using your mouse or touch screen
      </p>
    </div>
  );
};
